import { Controller, Form, FormProvider } from 'react-hook-form';
import {
  Box,
  Radio,
  Button,
  TextField,
  RadioGroup,
  FormControl,
  CircularProgress,
  FormHelperText,
  FormControlLabel,
} from '@mui/material';
import { t } from 'i18next';
import {
  BuildingSearchFormField,
  SelectFormField,
  TextFormField,
} from 'components/DynamicFormFields';
import { isNil } from 'lodash';
import {
  CustomModal,
  FormGooglePlacesAutocomplete,
  SaveChangesModal,
  USStateSelect,
} from '@components';
import { theme } from '@store';
import { useEditLocationModal } from './hooks/useEditLocationModal';

interface IEditLocationModalProps {
  patient_id: string;
  handleClose: () => void;
  isOpen: boolean;
}

export const EditLocationModal = ({
  isOpen,
  patient_id,
  handleClose,
}: IEditLocationModalProps) => {
  const {
    fullAddress,
    formMethods,
    isSubmitting,
    onSubmit,
    buildingUnitOptions,
    handleSelectLocation,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleDiscardChanges,
    handleSaveChanges,
    isValid,
    isEditSubmitting,
    handleCloseAction,
    isLoading,
  } = useEditLocationModal({ patient_id, handleClose });

  const { control, watch } = formMethods;

  const livesAtHome = watch('lives_at_home');
  const buildingRoom = watch('room');
  const atHomeCity = watch('city');

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={handleCloseAction}
      title={t('patient.detailsCard.editPatientLocation.title')}
      isLoading={isSubmitting}
    >
      {isLoading ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '590px',
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <FormProvider {...formMethods}>
          <Form
            control={control}
            onSubmit={e => onSubmit(e.event)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            <Controller
              name={'lives_at_home'}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <FormControl sx={{ width: '100%' }}>
                  <RadioGroup {...field} row>
                    <FormControlLabel
                      key={'Home option'}
                      value={'true'}
                      control={<Radio value={'true'} />}
                      label={t('patients.addModal.lives_at_home.home')}
                    />
                    <FormControlLabel
                      key={'Building option'}
                      value={'false'}
                      control={<Radio value={'false'} />}
                      label={t('patients.addModal.lives_at_home.building')}
                    />
                  </RadioGroup>
                  <FormHelperText error={!isNil(error)}>
                    {error ? error.message : null}
                  </FormHelperText>
                </FormControl>
              )}
            />
            {livesAtHome === 'true' ? (
              <>
                <FormGooglePlacesAutocomplete onChange={handleSelectLocation} />
                <TextFormField
                  name='street'
                  label={t('patients.addModal.lives_at_home.addressLine')}
                />
                <TextFormField
                  name='suite'
                  optional
                  label={t('patients.addModal.lives_at_home.addressLine2')}
                />
                <TextFormField
                  name='city'
                  label={t('patients.addModal.lives_at_home.city')}
                  value={atHomeCity}
                />
                <Controller
                  name='state'
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <USStateSelect
                      {...field}
                      error={!isNil(error)}
                      helperText={error ? error.message : ''}
                      required
                    />
                  )}
                />
                <TextFormField
                  name='zip_code'
                  label={t('patients.addModal.lives_at_home.zipCode')}
                />
                <TextFormField
                  name='county'
                  optional
                  label={t('patients.addModal.lives_at_home.county')}
                />
                <TextFormField
                  name='country'
                  label={t('patients.addModal.lives_at_home.country')}
                />
              </>
            ) : (
              <>
                <BuildingSearchFormField
                  name='building_id'
                  placeholder={t('patients.addModal.building_unit.building')}
                />
                {fullAddress && (
                  <TextField
                    value={fullAddress}
                    label={t(
                      'patients.addModal.building_unit.full_address_line',
                    )}
                    variant='outlined'
                    disabled
                    fullWidth
                  />
                )}
                <SelectFormField
                  name='building_unit'
                  label={t('patients.addModal.building_unit.buildingUnit')}
                  options={buildingUnitOptions}
                />
                <TextFormField
                  value={buildingRoom}
                  name='room'
                  label={t('patients.addModal.building_unit.room')}
                  optional
                />
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexDirection: 'row',
                    gap: '0.5rem',
                  }}
                >
                  <Controller
                    name='phone_number'
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        type='text'
                        label={t('patients.addModal.phone_number')}
                        variant='outlined'
                        error={!isNil(error)}
                        helperText={error ? error.message : null}
                        fullWidth
                      />
                    )}
                  />
                  <Controller
                    name='fax_number'
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        type='text'
                        label={t('patients.addModal.fax_number')}
                        variant='outlined'
                        error={!isNil(error)}
                        helperText={error ? error.message : null}
                        fullWidth
                      />
                    )}
                  />
                </Box>
              </>
            )}

            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '8px',
                position: 'sticky',
                bottom: 0,
                paddingY: 3,
                backgroundColor: theme.palette.common.white,
                zIndex: 3,
              }}
            >
              <Button
                variant='text'
                sx={{
                  fontSize: '0,875rem',
                  fontWeight: 'medium',
                  lineHeight: '24px',
                }}
                disabled={isSubmitting}
                onClick={handleCloseAction}
              >
                {t('general.cancel')}
              </Button>
              <Button
                type='submit'
                variant='contained'
                color='secondary'
                sx={{
                  fontSize: '0,875rem',
                  fontWeight: 'medium',
                  lineHeight: '24px',
                  color: 'white',
                }}
                disabled={isSubmitting || isEditSubmitting}
                loading={isEditSubmitting}
                loadingPosition='start'
              >
                {t(`general.${isEditSubmitting ? 'saving' : 'save'}`)}
              </Button>
            </Box>
            <SaveChangesModal
              isOpen={isSaveChangesModalOpen}
              onClose={handleCloseSaveChangesModal}
              isValid={isValid}
              handleSaveChanges={handleSaveChanges}
              discardChanges={handleDiscardChanges}
              isLoading={isEditSubmitting}
            />
          </Form>
        </FormProvider>
      )}
    </CustomModal>
  );
};
